/**
 * Site About → Webhooks → Delete (ENG-2427). `DELETE webhook_customer/{id}/`
 * removes the webhook and cascades its camera links. If any link carried
 * event types, those were in the connector settings, so the site needs a
 * Sync; a webhook no camera sent events to needs none.
 */
import React from "react";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  Hint,
  TS_COLORS,
  TsButton,
} from "../../Troubleshooting/TroubleshootingUi";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { SiteWebhook } from "./SiteAboutPageUtils/siteWebhooks";
import {
  WebhookLink,
  deleteNeedsSync,
  fetchSiteWebhookLinks,
} from "./SiteAboutPageUtils/webhookLinks";

export const DeleteSiteWebhookDialog = ({
  siteId,
  webhooks,
  navigate,
  onDeleted,
}: {
  siteId: number;
  webhooks: SiteWebhook[];
  navigate: NavigateFunction;
  onDeleted: () => void;
}) => {
  const deleteId = useSiteAboutStore((state) => state.deleteSiteWebhookId);
  const setDeleteId = useSiteAboutStore(
    (state) => state.setDeleteSiteWebhookId
  );
  const webhook = webhooks.find((candidate) => candidate.id === deleteId);
  const [links, setLinks] = React.useState<WebhookLink[] | null>(null);
  const [linksFailed, setLinksFailed] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (deleteId === null) return;
    let cancelled = false;
    setLinks(null);
    setLinksFailed(false);
    setError(null);
    fetchSiteWebhookLinks(siteId).then(
      (siteLinks) => {
        if (!cancelled) {
          setLinks(siteLinks.filter((link) => link.webhook?.id === deleteId));
        }
      },
      () => {
        if (!cancelled) setLinksFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [deleteId, siteId]);

  const close = () => setDeleteId(null);

  const remove = () => {
    if (deleteId === null) return;
    setDeleting(true);
    setError(null);
    createApi("")
      .delete(`webhook_customer/${deleteId}/`)
      .then(
        () => {
          setDeleting(false);
          // Unknown links (the lookup failed): flag a Sync to be safe.
          if (linksFailed || deleteNeedsSync(links ?? [])) {
            markSiteUnsynced(String(siteId));
          }
          close();
          onDeleted();
        },
        (reason: AxiosError) => {
          setDeleting(false);
          const status = reason?.response?.status;
          if (status === 401) {
            universalAPIErrorHandler(reason, navigate, remove, () =>
              setError("Deleting the webhook failed. Please try again.")
            );
            return;
          }
          setError(
            status === 403
              ? "You don't have permission to delete webhooks."
              : status === 404
              ? "This webhook is already gone."
              : "Deleting the webhook failed. Please try again."
          );
          if (status === 404) onDeleted();
        }
      );
  };

  const cameraCount = links
    ? new Set(links.map((link) => link.stream.camera)).size
    : null;

  return (
    <Dialog
      open={deleteId !== null}
      onClose={deleting ? undefined : close}
      aria-labelledby="delete-site-webhook-title"
    >
      <DialogTitle
        id="delete-site-webhook-title"
        sx={{ ...MULISH, fontWeight: 800, color: TS_COLORS.navy }}
      >
        Delete webhook{webhook ? ` "${webhook.display_name}"` : ""}?
      </DialogTitle>
      <DialogContent>
        <Hint sx={{ fontSize: 14, display: "block" }}>
          {linksFailed
            ? "Couldn't check which cameras use it. Its camera links are deleted with it; Sync the site afterwards."
            : cameraCount === null
            ? "Checking which cameras use it…"
            : cameraCount === 0
            ? "No cameras send to this webhook."
            : `${cameraCount} camera${
                cameraCount === 1 ? "" : "s"
              } send to this webhook and will stop. Sync the site afterwards.`}
        </Hint>
        {error && (
          <Hint sx={{ color: TS_COLORS.red, display: "block", mt: 1.5 }}>
            {error}
          </Hint>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <TsButton variant="quiet" onClick={close} disabled={deleting}>
          Cancel
        </TsButton>
        <TsButton
          color={TS_COLORS.red}
          onClick={remove}
          disabled={deleting || (links === null && !linksFailed)}
        >
          {deleting ? "Deleting…" : "Delete webhook"}
        </TsButton>
      </DialogActions>
    </Dialog>
  );
};
